import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Dropdown } from 'antd';
import { TbBrandOauth, TbCopy, TbDotsVertical, TbEdit, TbExternalLink, TbTrash } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownMenuPanel, SmallTooltip } from '@/shared';
import CredentialActionGroup from '@/shared/components/groups/CredentialActionGroup';
import RenameResourceModal from '@/shared/components/modals/RenameResourceModal';
import { DeletePrimitiveModal } from '@/shared/components/modals/ResourceActionModals';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { formatDate } from '@/utilities';

import DeveloperCredentialListItem from '../../../components/DeveloperCredentialListItem';

export interface OAuthApplicationListRecord {
    clientId: string;
    createdAt?: string;
    iconUrl?: string;
    id: string;
    installationUrl?: string;
    name: string;
    shortDescription?: string;
    status?: string;
}

interface OAuthApplicationListItemProps {
    item: OAuthApplicationListRecord;
    onDeleted?: (oauthApplicationId: string) => void;
    onUpdated?: (oauthApplication: OAuthApplicationListRecord) => void;
}

const OAuthApplicationListItem: FC<OAuthApplicationListItemProps> = ({ item, onDeleted, onUpdated }) => {
    const { token } = useAntDesignTheme();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const maskedClientId = `${item.clientId.slice(0, 8)}...${item.clientId.slice(-4)}`;
    const statusColor = {
        Approved: 'green',
        Draft: 'default',
        Rejected: 'red',
        Submitted: 'blue',
    }[item.status || 'Draft'];

    const deleteApplication = async () => {
        try {
            await authorizedAxiosInstance.delete(`/api/oauth-applications/${item.id}`);
            onDeleted?.(item.id);
            toast('OAuth Application Deleted', { description: item.name });
        } catch (error) {
            captureException(error);
            toast.error('Unable to delete OAuth application');
            throw error;
        }
    };

    return (
        <>
            <DeveloperCredentialListItem
                itemKey={item.id}
                extra={
                    <CredentialActionGroup
                        maskedValue={maskedClientId}
                        initialValue={item.clientId}
                        copyMessage='Client ID Copied'
                    >
                        <SmallTooltip
                            title={item.installationUrl ? 'Open integration' : 'No installation URL configured'}
                        >
                            <Button
                                aria-label='Open integration'
                                size='small'
                                disabled={!item.installationUrl}
                                icon={<TbExternalLink />}
                                onClick={() => window.open(item.installationUrl, '_blank', 'noopener,noreferrer')}
                            />
                        </SmallTooltip>
                        <Dropdown
                            trigger={['click']}
                            placement='bottomRight'
                            popupRender={() => (
                                <DropdownMenuPanel
                                    {...{
                                        items: [
                                            {
                                                key: 'copy',
                                                icon: <TbCopy size='1rem' />,
                                                label: 'Copy Client ID',
                                            },
                                            { key: 'rename', icon: <TbEdit size='1rem' />, label: 'Rename' },
                                            { type: 'divider' },
                                            {
                                                key: 'delete',
                                                danger: true,
                                                icon: <TbTrash size='1rem' />,
                                                label: 'Delete',
                                            },
                                        ],
                                        onClick: ({ key }) => {
                                            if (key === 'copy') {
                                                void navigator.clipboard.writeText(item.clientId);
                                                toast('Client ID Copied');
                                            }
                                            if (key === 'rename') {
                                                setRenameOpen(true);
                                            }
                                            if (key === 'delete') {
                                                setDeleteOpen(true);
                                            }
                                        },
                                    }}
                                />
                            )}
                        >
                            <Button
                                aria-label='OAuth application actions'
                                size='small'
                                icon={<TbDotsVertical style={{ color: token.colorTextTertiary }} />}
                            />
                        </Dropdown>
                    </CredentialActionGroup>
                }
                avatarIcon={<TbBrandOauth size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                avatarSrc={item.iconUrl}
                name={item.name}
                status={item.status || 'Draft'}
                statusColor={statusColor}
                description={item.createdAt ? `Created ${formatDate(new Date(item.createdAt))}` : undefined}
            />
            <RenameResourceModal
                open={renameOpen}
                currentName={item.name}
                resourceLabel='OAuth Application'
                onCancel={() => setRenameOpen(false)}
                onRename={async (name) => {
                    const { data } = await authorizedAxiosInstance.put<OAuthApplicationListRecord>(
                        `/api/oauth-applications/${item.id}`,
                        { name },
                    );
                    onUpdated?.(data);
                    setRenameOpen(false);
                    toast('OAuth Application Renamed', { description: name });
                }}
            />
            <DeletePrimitiveModal
                open={deleteOpen}
                resourceLabel='OAuth Application'
                resourceName={item.name}
                warning='Deleting this OAuth application permanently invalidates its client and existing authorizations.'
                onCancel={() => setDeleteOpen(false)}
                onDelete={deleteApplication}
            />
        </>
    );
};

export default OAuthApplicationListItem;
